import React, { Dispatch, Fragment, SetStateAction } from "react";
import { Field, Form, Formik } from "formik";
import { makeStyles } from "@mui/styles";
import { Brand, CredentialResponse, CredentialsStatusActions, FormActionStatus, LoginSupportInfoFormActions, PendingActions, UpdateUserCredentialsRequest, UserPermissions } from "../../services/Types";
import UserCredentialsApi from "../../services/Api";
import { ValidationError } from "../../services/Errors";
import { emailUpdateFormValidationSchema } from "../../services/ValidationSchemas";
import PermissionCheck from "../PermissionCheck";
import { Button, Link, TextField } from "@mui/material";
import FormActionStatusIcon from "../FormActionStatusIcon/FormActionStatusIcon";

const useStyles = makeStyles({
  emailNotVerified: {
    float: "right",
  },
  emailNotVerifiedIcon: {
    color: "#FFCC66",
    fontSize: "24px!important",
  },
  emailVerifiedIcon: {
    color: "#53934F",
    fontSize: "24px!important",
  },
  emailActionButtons: {
    marginTop: "11px",
  },
  verificationEmailAndStatusHolder: {
    marginTop: "-3px",
  },
  fieldErrorsHolder: {
    position: "relative",
    padding: "0 0 7px 25px",
    color: "#D80443",
    "& i": {
      position: "absolute",
      top: "0",
      left: "0",
    },
  },
  markVerifiedButton: {
    "&.MuiButton-root": {
      backgroundColor: "#FFF7E8",
      border: "1px solid #FFCC66",
    },
  },
});

export const LoginEmailForm = ({
  userCredentials,
  updateFormAction,
  userCredentialsApi,
  activeEmail,
  setActiveEmail,
  setUserCredentials,
  setUserCredentialsPendingActions,
  handleTabDataReload,
  userPermissions,
  isOktaUser,
  formActionsStatus,
  setIsMarkVerifiedCollapseOpened,
  executeFormAction,
  brand
}:{
  userCredentials: CredentialResponse,
  updateFormAction: any,
  userCredentialsApi: UserCredentialsApi,
  activeEmail: string,
  setActiveEmail: Dispatch<SetStateAction<string>>,
  setUserCredentials: Dispatch<SetStateAction<CredentialResponse | null>>,
  setUserCredentialsPendingActions: Dispatch<SetStateAction<PendingActions>>,
  handleTabDataReload: any,
  userPermissions: Array<UserPermissions>,
  isOktaUser: boolean,
  formActionsStatus: LoginSupportInfoFormActions,
  setIsMarkVerifiedCollapseOpened: Dispatch<SetStateAction<boolean>>,
  executeFormAction: any,
  brand?: Brand
}) => {
  const classes = useStyles();

  const updateEmail = async (
    updateCredentialsRequest: UpdateUserCredentialsRequest,
    setErrors: any
  ): Promise<any> => {
    updateFormAction("verifyOrUpdateEmail", FormActionStatus.IN_PROGRESS);

    try {
      const response = await userCredentialsApi.updateUserCredentials(
        updateCredentialsRequest
      );
      
      updateFormAction("verifyOrUpdateEmail", FormActionStatus.DONE);

      if (activeEmail !== response.email) {
        setUserCredentials(
          (prevUserCredentials) =>
            prevUserCredentials && {
              ...prevUserCredentials,
              emailVerified: activeEmail === response.email,
            }
        );
        setUserCredentialsPendingActions((prevVal: PendingActions) => {
          //emailVerified added to array because it will be re-added below
          const actionsToRemove = [CredentialsStatusActions.emailMissing, CredentialsStatusActions.emailDuplicated, CredentialsStatusActions.emailVerified];

          return {
            optional: [
              ...prevVal.optional.filter(action => !actionsToRemove.includes(action))
            ],
            require: [
              CredentialsStatusActions.emailVerified,
              ...prevVal.require.filter(action => !actionsToRemove.includes(action))
            ],
          };
        });
        setActiveEmail(response.email);
      }      

      setTimeout(() => {
        updateFormAction("verifyOrUpdateEmail", FormActionStatus.NO_ACTION);
      }, 1000);

      await handleTabDataReload();
      return response;
    } catch (error) {
      console.error("Update email error: ", error);
      updateFormAction("verifyOrUpdateEmail", FormActionStatus.ERROR);
      setTimeout(() => {
        updateFormAction("verifyOrUpdateEmail", FormActionStatus.NO_ACTION);
      }, 2000);
      if (error instanceof ValidationError) {
        setErrors({
          email: error.getReasons().join("<br/>"),
        });
      }
    }
  };

  return (
    <Formik
      initialValues={{
        email: userCredentials.email,
        reEnteredEmail: "",
      }}
      validationSchema={emailUpdateFormValidationSchema}
      onSubmit={(values, actions) => {
        updateEmail(
          {
            id: userCredentials.id,
            email: values.email,
          },
          actions.setErrors
        )
      }}
    >
      {({ errors, values, handleChange, handleSubmit }) => {
        return (
        <Form onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email">Email</label>
            <Field
              component={TextField}
              name="email"
              id="email"
              type="text"
              variant="outlined"
              fullWidth
              value={values.email}
              onChange={handleChange}
            />
            {errors.email && (
              <div>
                <div className={classes.fieldErrorsHolder}>
                  <i className="t-error"></i>
                  {errors.email}
                </div>
              </div>
            )}
            <PermissionCheck
              userPermissions={userPermissions}
              permissionToCheck={
                UserPermissions.CHANGE_EMAIL_EDIT
              }
            >
              <label htmlFor="reEnteredEmail">Re-enter Email</label>
              <Field
                component={TextField}
                name="reEnteredEmail"
                id="reEnteredEmail"
                type="text"
                variant="outlined"
                fullWidth
                value={values.reEnteredEmail}
                onChange={handleChange}
              />
              {errors.reEnteredEmail && (
                <div>
                  <div className={classes.fieldErrorsHolder} data-testid="reEnterEmailError">
                    <i className="t-error"></i>
                    {errors.reEnteredEmail}
                  </div>
                </div>
              )}
            </PermissionCheck>
          </div>
          {
            !isOktaUser &&
            <div
              className={`${classes.verificationEmailAndStatusHolder} icon-text-holder`}
            >
              <PermissionCheck
                userPermissions={userPermissions}
                permissionToCheck={
                  UserPermissions.SEND_VERIFY_EMAIL_EDIT
                }
              >
                {!userCredentials?.emailVerified && <Fragment>
                  <i
                    className={`t-send`}
                    style={{
                      color: brand?.actionItemColor || "#006C7D",
                    }}
                  ></i>
                  <Link
                    data-testid="verifyEmailLink"
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && executeFormAction({
                      credentialId: userCredentials.id,
                      action: "sendVerificationEmail",
                      apiMethodToCall: userCredentialsApi.sendEmailVerificationEmail,
                    })}
                    onClick={() => executeFormAction({
                      credentialId: userCredentials.id,
                      action: "sendVerificationEmail",
                      apiMethodToCall: userCredentialsApi.sendEmailVerificationEmail,
                    })
                    }
                  >
                    Send Verification Email
                  </Link>
                </Fragment>}
              </PermissionCheck>
              <FormActionStatusIcon
                brand={brand}
                status={formActionsStatus.sendVerificationEmail}
              />
              {userCredentials?.emailVerified ? (
                <span
                  className="icon-text-holder"
                >
                  <i
                    className={`${classes.emailVerifiedIcon} t-check-circle`}
                  ></i>
                  <span>Verified</span>
                </span>
              ) : (
                <span
                  className={`${classes.emailNotVerified} icon-text-holder`}
                >
                  <i
                    className={`${classes.emailNotVerifiedIcon} t-warning`}
                  ></i>
                  <span>Not Verified</span>
                </span>
              )}
            </div>
          }
          <div className={classes.emailActionButtons}>
            <PermissionCheck
              userPermissions={userPermissions}
              permissionToCheck={
                UserPermissions.CHANGE_EMAIL_EDIT
              }
            >
              <Button
                type="submit"
                variant="outlined"
              >
                Update Email
              </Button>
            </PermissionCheck>
            {
              !isOktaUser &&
              <PermissionCheck
                userPermissions={userPermissions}
                permissionToCheck={
                  UserPermissions.VERIFY_EMAIL_ADDRESS_EDIT
                }
              >
                {!userCredentials?.emailVerified && (
                  <Button
                    className={classes.markVerifiedButton}
                    variant="outlined"
                    onClick={() => {
                      setIsMarkVerifiedCollapseOpened(
                        (previousValue) => !previousValue
                      );
                    }}
                  >
                    Mark Verified
                  </Button>
                )}
              </PermissionCheck>
            }
            <FormActionStatusIcon
              brand={brand}
              status={formActionsStatus.verifyOrUpdateEmail}
            />
          </div>
        </Form>
        )}}
      </Formik>
  );
}